import React from 'react';
import { Menu, Item, Text, Divider } from '@adobe/react-spectrum';
import { Selection } from '@react-types/shared';
import MenuCheckbox from './MenuCheckbox';
import styles from './FilterSection.module.scss';

// CSME-113550: key of the divider Item separating risk-level/In Planning options from
// the independent 'Completed' visibility toggle below. Must stay in Menu's disabledKeys
// so it is never itself selectable (mirrors CategoryProductFilter.tsx divider pattern).
const COMPLETED_DIVIDER_KEY = 'status-completed-divider';

interface StatusOption {
  key: string;
  label: string;
}

interface StatusFilterProps {
  statusOptions: StatusOption[];
  selectedStatusKeys: Selection;
  onSelectionChange: (selection: Selection) => void;
  onAction?: (key: React.Key) => void;
}

export function StatusFilter({
  statusOptions,
  selectedStatusKeys,
  onSelectionChange,
  onAction
}: StatusFilterProps) {
  return (
    <Menu
      data-testid="filter-section-status-menu"
      selectedKeys={selectedStatusKeys}
      onSelectionChange={onSelectionChange}
      onAction={onAction}
      selectionMode="multiple"
      disabledKeys={[COMPLETED_DIVIDER_KEY]}
      UNSAFE_className={styles['subMenu']}
    >
      {[
        ...(statusOptions.length
          ? statusOptions.map(option => (
              <Item
                key={option.key}
                data-testid={`filter-section-status-option-${option.key}`}
              >
                <Text UNSAFE_className={styles.menuItemText}>
                  <MenuCheckbox
                    isSelected={
                      selectedStatusKeys instanceof Set
                        ? selectedStatusKeys.has(option.key)
                        : false
                    }
                  />
                  <span className={styles.menuItemLabel} title={option.label}>
                    {option.label.includes('Risk Level :') ? (
                      <>
                        Risk Level :{' '}
                        <strong>
                          {option.label.replace('Risk Level : ', '')}
                        </strong>
                      </>
                    ) : (
                      option.label
                    )}
                  </span>
                </Text>
              </Item>
            ))
          : [
              <Item key="no-status">
                <Text>No Status Options Available</Text>
              </Item>
            ]),
        // CSME-113550: visual divider segregating risk-level/In Planning from 'Completed'
        // (reuses the divider pattern from CategoryProductFilter.tsx:378-386).
        <Item key={COMPLETED_DIVIDER_KEY}>
          <Text>
            <Divider
              orientation="horizontal"
              UNSAFE_className={styles.subMenuDivider}
            />
          </Text>
        </Item>,
        // CSME-113550: 'Completed' toggles the independent hideCompletedItems preference
        // (checked = shown, i.e. hideCompletedItems === false). Not part of statusOptions/
        // selectedStatus — handled specially by the caller's onSelectionChange.
        <Item
          key="completed"
          data-testid="filter-section-status-option-completed"
        >
          <Text UNSAFE_className={styles.menuItemText}>
            <MenuCheckbox
              isSelected={
                selectedStatusKeys instanceof Set
                  ? selectedStatusKeys.has('completed')
                  : false
              }
            />
            <span className={styles.menuItemLabel} title="Completed">
              Completed
            </span>
          </Text>
        </Item>
      ]}
    </Menu>
  );
}

StatusFilter.displayName = 'StatusFilter';
